<template>
  <div class="card-list">
    <div class="card-list__wrap">
      <TourCardListSkeleton>
        <template
          v-for="(exp, index) in expList"
          :key="exp.id"
        >
          <ExpCard
            :exp="exp"
            :exp-snippet-index="index"
            :exp-url-params="selectedRubricUrl"
            :context-geo-subject="contextGeoSubject"
            :is-country="isCountry"
            :is-user-admin="userStore.isAdmin"
            :target-blank="isTargetBlank"
            :type-label="getTypeLabel(exp.geo)"
            :sogu-block="soguBlock"
            sogu-page="listing"
            :additional-sogu-params="listingCardSoguParams"
            class="exp-snippet"
            :allow-show-promotion-badge="true"
            :tooltip-teleport-target="targetModalElem"
            :is-in-favorites="isFavorite(exp.id)"
            is-show-favorite-btn
            :isOptimizedImage="getIsOptimizedImage(index)"
            :showMiniDiscount="isExpCardMiniDiscountActive"
            @toggle-favorite="toggleFavoriteHandle(exp)"
            @right-clicked="handleRightClick($event, exp)"
          />
          <slot :index="index" />
        </template>
      </TourCardListSkeleton>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ExpCard } from '@experience/exp-card';
  import type { Experience } from '@experience/exp-card/dist/types/experience';
  import type { ContextGeoSubject } from '@/shared/types/geography.ts';
  import type { RecommendationListingSoguParams } from '@/shared/types/recommendationListingSogu';
  import { useUserStore } from '@/entities/user';
  import { useScreenSize } from '@experience/shared';
  import { useToggleExperienceFavorite } from '@/features/experience-favorite';
  import { useExperienceFavorites } from '@/entities/experience/favorite';
  import TourCardListSkeleton from './TourCardListSkeleton.vue';
  import { trackEvent } from '@/entities/sogu';
  import { LocationType, useTourLabel } from '@/entities/location';
  import { mapExperienceDataToSogu } from '@/entities/experience/sogu';
  import { useToursExperienceFavoriteSogu } from '@/features/experience-favorite/composables/tour/useToursExperienceFavoriteSogu.ts';
  import { useTourFiltersStore } from '@/entities/filter/store/tour';
  import { useExpCardMiniDiscount } from '@/features/experiments/composables/useExpCardMiniDiscount';
  import { computed, ref, type useShadowRoot } from 'vue';

  interface Props {
    expList: Experience[];
    contextGeoSubject: ContextGeoSubject;
    location?: LocationType;
    soguBlock?: string;
    recommendationListingSoguParams?: RecommendationListingSoguParams;
  }

  const props = withDefaults(defineProps<Props>(), {
    soguBlock: 'experiences',
  });

  const userStore = useUserStore();

  const { isMobile, isTablet } = useScreenSize();
  const { isFavorite } = useExperienceFavorites();
  const { trackExperiencesFavoriteClick } = useToursExperienceFavoriteSogu();
  const { toggle } = useToggleExperienceFavorite();
  const { getTypeLabel } = useTourLabel(computed(() => props.location));
  const filtersStore = useTourFiltersStore();

  const isTargetBlank = computed(() => !isMobile.value && !isTablet.value);

  const getIsOptimizedImage = (index: number) => {
    if (isMobile.value) {
      return index === 0;
    }

    return [0, 1].includes(index);
  };

  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);
  const modalTarget = ref<HTMLElement | null>(null);

  if (rootNode) {
    modalTarget.value = rootNode.querySelector('.modal-target');
  }

  const targetModalElem = computed(() => modalTarget.value || document.body);

  const selectedRubricUrl = computed(() => {
    const rubric = filtersStore.state.tag__slugs;

    return rubric && rubric !== 'all' ? `?rubric=${rubric}` : '';
  });

  const listingCardSoguParams = computed(() => props.recommendationListingSoguParams ?? {});

  const handleRightClick = (clickType: string, slide: Experience) => {
    const payload = {
      clickType,
      ...props.contextGeoSubject.soguStamp,
      ...mapExperienceDataToSogu(slide),
    };
    trackEvent('listing:experiences:experience.right_click', {
      ...payload,
    });
  };

  const toggleFavoriteHandle = (exp: Experience) => {
    trackExperiencesFavoriteClick(exp, props.contextGeoSubject);
    toggle(exp.id);
  };

  const isCountry = computed(() => props.location === LocationType.COUNTRY);
  const { isExpCardMiniDiscountActive } = useExpCardMiniDiscount();
</script>

<style scoped lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  :has(.paused-output) .card-list {
    margin-top: 32px;
  }

  .card-list__wrap {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;

    @supports (display: grid) {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 40px 24px;

      @include adaptivity.tablet {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      @include adaptivity.mobile {
        grid-template-columns: minmax(0, 1fr);
        gap: 26px 24px;
      }

      @media only screen and (min-width: 1024px) and (max-width: 1075px) {
        grid-template-columns: repeat(auto-fit, minmax(300px, 0.333fr));
      }
    }
  }
</style>
